import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import useChatUnreadCounts from 'hooks/useChatUnreadCounts';
import { chatQueryKeys, getChatConversations } from 'services/api/chat';

/**
 * The referral's chat channels (OLIO-2832): one conversation per facility the referral
 * was sent to, policy-scoped server-side so only channels the user can see come back.
 * Mounted at page level so the Chat tab badge reads from the same cache.
 *
 * The channel set can change mid-session (facility add/remove), so this refetches on the
 * set-change flag and on window focus. Unread counts are their own query
 * (useChatUnreadCounts) merged in AT RENDER — each cache has exactly one writer, and the
 * memo recomputes whenever either query lands.
 */
export default function useChatConversations(id: string | undefined) {
  const query = useQuery({
    queryKey: chatQueryKeys.index(id ?? 'missing'),
    queryFn: ({ signal }) => getChatConversations(id!, signal),
    enabled: !!id,
    // The channel set can change mid-session (facility add/remove) — the set-change flag
    // is the live signal; the focus refetch is the independent net.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const { countsById, mentionedById } = useChatUnreadCounts(id);

  const list = query.data;
  const conversations = useMemo(
    () =>
      (list ?? []).map((conversation) => ({
        ...conversation,
        unreadCount: countsById.get(conversation.id) ?? 0,
        unreadMentioned: mentionedById.get(conversation.id) ?? false,
      })),
    [list, countsById, mentionedById]
  );

  return {
    conversations,
    isLoading: query.isPending && !!id,
    isError: query.isError,
  };
}
